<template>
  <aside
    class="h-screen fixed top-0 lg:border-r-0.5 lg:border-grey-11 dark:lg:border-dark-border transition-all duration-300 ease-in-out z-10"
    :class="[
      'w-full',
      {
        'bg-white dark:bg-dark-surface': !hasExpandedSubmenu,
        'bg-white lg:bg-grey-10 dark:bg-dark-surface dark:lg:bg-dark-bg': hasExpandedSubmenu,
        'lg:w-16': !sidebarIsExpanded,
        'lg:w-80': sidebarIsExpanded,
        'hidden lg:block': !sidebarIsExpanded,
      },
    ]"
    role="complementary"
    aria-label="Main Navigation"
  >
    <div
      :class="[
        !sidebarIsExpanded ? 'px-2' : 'px-2 lg:px-4',
        'h-full lg:mt-12 flex flex-col',
      ]"
    >
      <!-- Navigation Controls -->
      <nav aria-label="Navigation Controls">
        <ul
          class="pb-4 pt-4 lg:pt-0 flex items-center justify-between w-full"
          :class="[sidebarIsExpanded ? '' : 'flex-col gap-2']"
          role="menubar"
        >
          <!-- Back button (expanded sidebar) -->
          <li
            v-if="hasExpandedSubmenu && sidebarIsExpanded"
            role="none"
            class="w-full flex"
          >
            <AppButton
              theme="secondary"
              icon="arrow-left"
              icon-position="start"
              :class="[
                'lg:w-full mr-4 !p-2',
                hasExpandedSubmenu ? 'lg:bg-white dark:lg:bg-dark-surface' : '',
              ]"
              :aria-label="backButtonLabel"
              role="menuitem"
              @click="handleBackClick"
              @keydown.enter="handleBackClick"
              @keydown.space.prevent="handleBackClick"
            >
              <span class="px-2">Back</span>
            </AppButton>
          </li>
          
          <!-- Back button (collapsed sidebar) -->
          <li
            v-else-if="hasExpandedSubmenu"
            role="none"
            class="hidden lg:flex"
            :class="[!sidebarIsExpanded ? 'w-full' : '']"
          >
            <AppButton
              theme="secondary"
              class="w-fit rounded-full !p-3 bg-white dark:bg-dark-surface mx-auto order-last"
              :aria-label="backButtonLabel"
              role="menuitem"
              @click="handleBackClick"
              @keydown.enter="handleBackClick"
              @keydown.space.prevent="handleBackClick"
            >
              <mdicon aria-hidden="true" :size="16" name="arrow-left" />
            </AppButton>
          </li>
          
          <!-- Expand/Collapse sidebar button -->
          <li
            role="none"
            class="hidden lg:flex"
            :class="[
              sidebarIsExpanded && !hasExpandedSubmenu
                ? 'ml-auto'
                : 'justify-center',
              !sidebarIsExpanded ? 'w-full order-first' : '',
            ]"
          >
            <AppButton
              theme="secondary"
              :class="[
                'w-fit rounded-full !p-3',
                hasExpandedSubmenu ? 'bg-white dark:bg-dark-surface' : '',
                !sidebarIsExpanded ? 'mx-auto' : '',
              ]"
              :aria-expanded="sidebarIsExpanded"
              :aria-label="
                sidebarIsExpanded ? 'Collapse sidebar' : 'Expand sidebar'
              "
              role="menuitem"
              @click="sidebarStore.toggleExpanded"
              @keydown.enter.prevent="sidebarStore.toggleExpanded"
              @keydown.space.prevent="sidebarStore.toggleExpanded"
            >
              <mdicon
                aria-hidden="true"
                :size="16"
                :name="sidebarIsExpanded ? 'page-first' : 'page-last'"
              />
            </AppButton>
          </li>
        </ul>
      </nav>
            
      <!-- Navigation Items -->
      <nav aria-label="Navigation Menu" class="relative overflow-hidden">
        <Transition :name="transitionName" mode="out-in">
          <ul
            :key="navigationKey"
            class="border-grey-7 dark:border-dark-border py-4"
            :class="[
              hasExpandedSubmenu
                ? 'border-y-0.5'
                : 'border-b-0.5 lg:border-y-0.5',
            ]"
            role="menubar"
          >
            <!-- Render top-level items -->
            <AppSidebarNavItemRecursive
              v-for="item in visibleItems"
              :key="getItemKey(item)"
              :item="item"
              :level="0"
              role="none"
            />
            
            <!-- Render expanded submenu items -->
            <AppSidebarSubmenuRecursive
              v-if="currentExpandedItem && currentExpandedItem.children"
              :items="currentExpandedItem.children"
              :level="0"
              :menu-id="`submenu-${getItemKey(currentExpandedItem)}`"
            />
          </ul>
        </Transition>
      </nav>
    </div>
  </aside>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useSidebarNavigation } from '../../../composables/useSidebarNavigation.js'
import AppSidebarNavItemRecursive from './AppSidebarNavItemRecursive.vue'
import AppSidebarSubmenuRecursive from './AppSidebarSubmenuRecursive.vue'

const router = useRouter()
const { sidebarStore, getItemKey } = useSidebarNavigation()

const { sidebarIsExpanded, hasExpandedSubmenu, currentExpandedItem } =
  storeToRefs(sidebarStore)

// Track navigation direction for animations
const transitionName = ref('slide-left')
const previousHistoryLength = ref(0)

/**
 * Unique key for transition to trigger animation
 */
const navigationKey = computed(() => {
  return sidebarStore.navigationHistory.join('-') || 'root'
})

/**
 * Watch navigation history to determine animation direction
 */
watch(
  () => sidebarStore.navigationHistory.length,
  (newLength) => {
    if (newLength > previousHistoryLength.value) {
      // Going deeper - slide left
      transitionName.value = 'slide-left'
    } else if (newLength < previousHistoryLength.value) {
      // Going back - slide right
      transitionName.value = 'slide-right'
    }
    previousHistoryLength.value = newLength
  }
)

/**
 * Get visible items based on expansion state
 */
const visibleItems = computed(() => {
  // If a submenu is expanded, don't show top-level items
  if (hasExpandedSubmenu.value) {
    return []
  }
  return sidebarStore.userNavigationItems
})

/**
 * Generate accessible label for back button
 */
const backButtonLabel = computed(() => {
  const historyLength = sidebarStore.navigationHistory.length
  if (historyLength > 1) {
    return 'Go back to previous menu'
  }
  return 'Go back to main menu'
})

/**
 * Handle back button click - collapse current submenu level
 */
const handleBackClick = () => {
  const redirectPath = sidebarStore.collapseSubmenu()
  if (redirectPath) {
    router.push(redirectPath)
  }
}

// Initialize navigation items on mount
sidebarStore.initialiseNavigationItems()
</script>

<style scoped>
/* Slide Left Animation (going deeper) */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Slide Right Animation (going back) */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>
